import { FC } from 'react';
import { PrismicRichText, PrismicLink, PrismicImage } from '@prismicio/react';
import { Content } from '@prismicio/client';
import { ArrowRightIcon } from '@/assets';
import dynamic from 'next/dynamic';

const AnimatedCirclesBackground = dynamic(
  () => import('@/components/AnimatedCirclesBackground'),
  { ssr: false },
);

type Props = {
  slice: Content.IntroMediaGridSlice;
};

const DefaultVariation: FC<Props> = ({ slice }) => {
  const {
    title,
    description,
    primary_cta: primaryCta,
    media_items: mediaItems,
  } = slice.primary;

  return (
    <section
      className="relative overflow-hidden"
      data-slice-type={slice.slice_type}
    >
      <AnimatedCirclesBackground />

      <div className="relative z-10 container mx-auto px-4 py-10 md:py-24 text-white">
        <div className="mx-auto text-left">
          {/* Title & Description */}
          <div className="mb-8">
            {title && (
              <div className="mb-6">
                <PrismicRichText
                  field={title}
                  components={{
                    heading1: ({ children }) => (
                      <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium leading-tight font-inter">
                        {children}
                      </h1>
                    ),
                  }}
                />
              </div>
            )}
            {description && (
              <div>
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="opacity-90 font-normal">{children}</p>
                    ),
                  }}
                />
              </div>
            )}
          </div>

          {/* CTA */}
          {primaryCta?.text && (
            <PrismicLink field={primaryCta} className="secondary-button">
              <ArrowRightIcon className="arrow-icon" />
              {primaryCta.text || 'Learn More'}
            </PrismicLink>
          )}
        </div>
      </div>

      {/* Media Grid */}
      {mediaItems?.length > 0 && (
        <div className="relative z-10 container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-6 max-w-5xl mx-auto">
            {/* Large Image */}
            {mediaItems[0]?.media && (
              <div className="flex-1 md:w-2/3">
                <div className="relative overflow-hidden rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                  <PrismicImage
                    field={mediaItems[0].media}
                    className="w-full h-64 md:h-80 object-cover"
                  />
                </div>
              </div>
            )}

            {/* Two Smaller Images */}
            <div className="flex flex-col gap-6 md:w-1/3">
              {mediaItems.slice(1, 3).map(
                (item, index) =>
                  item.media && (
                    <div
                      key={index}
                      className={`relative overflow-hidden rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 ${index % 2 !== 0 ? 'mt-1.5' : ''}`}
                    >
                      <PrismicImage
                        field={item.media}
                        className="w-full h-28 md:h-36 object-cover"
                      />
                    </div>
                  ),
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DefaultVariation;
